import { Button, Card, message, Table, Tooltip, Typography } from 'antd';
import React, { useState } from 'react';
import { TableColumnTitle } from '../../../../shared/globalStyling/styledText';
import TagMetricCards from './TagMetricCards';
import TagsPagination from '../../main/components/TagsPagination';
import '../../styles/index.scss';

const { Text } = Typography;

const TestLogsCard = ({
  logs,
  hasBasketItems,
  details,
  hasSignups,
  testTag,
  loading,
  type,
  retailIds,
  basicTagActivityData,
  persistedTagActivityData,
  generatedTag,
}) => {
  const [expandedRowKeys, setExpandedRowKeys] = React.useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const handleExpand = (expanded, record) => {
    if (logs.indexOf(record) !== 0) return;
    setExpandedRowKeys(expanded ? [record.key || 0] : []);
  };

  const hasRetailIdData = () => {
    const retailIdPersisted = generatedTag?.latestRetailIds;
    return retailIdPersisted && retailIdPersisted.length > 0;
  };

  const expandedRowRender = (record) => {
    const retailIdPersisted = generatedTag?.latestRetailIds?.map((item, index) => ({
      id: index.toString(),
      retailId: String(item.retailId || item.retailID || ''),
      pageViews: Number(item.pageViews || item.pageviews || 0),
      signups: Number(item.signups || 0),
      transactions: Number(item.transactions || 0),
      total: Number(item.total || 0),
      sessions: Number(item.sessions || 0),
    })) || [];

    if (!retailIdPersisted.length) {
      return (
        <div className="tags-test-logs__no-data">
          <Text type="secondary">No retail ID activity data available. Click "Test Signal" to fetch data.</Text>
        </div>
      );
    }

    const eventColumns = [
      {
        title: <TableColumnTitle text={'Retail ID'} />,
        dataIndex: 'retailId',
        key: 'retailId',
        width: 120,
        render: (retailId) => (
          <Text className="tags-test-logs__retail-id">
            {retailId}
          </Text>
        )
      },
      {
        title: <TableColumnTitle text={'Page Views'} />,
        dataIndex: 'pageViews',
        key: 'pageViews',
        width: 100,
        render: (pageViews) => (
          <Text className="tags-test-logs__metric">
            {pageViews}
          </Text>
        )
      },
      {
        title: <TableColumnTitle text={'Transactions'} />,
        dataIndex: 'transactions',
        key: 'transactions',
        width: 100,
        render: (transactions) => (
          <Text className="tags-test-logs__metric">
            {transactions}
          </Text>
        )
      },
      {
        title: <TableColumnTitle text={'Signups'} />,
        dataIndex: 'signups',
        key: 'signups',
        width: 80,
        render: (signups) => (
          <Text className="tags-test-logs__metric">
            {signups}
          </Text>
        )
      },
      {
        title: <TableColumnTitle text={'Total'} />,
        dataIndex: 'total',
        key: 'total',
        width: 80,
        render: (total) => (
          <Text className="tags-test-logs__total">
            {total}
          </Text>
        )
      },
      {
        title: <TableColumnTitle text={'Sessions'} />,
        dataIndex: 'sessions',
        key: 'sessions',
        width: 80,
        render: (sessions) => (
          <Text className="tags-test-logs__metric">
            {sessions}
          </Text>
        )
      }
    ];

    return (
      <div className="tags-test-logs__expandable-row">
        <div className="tags-test-logs__table-container">
          {/* Events Table Section */}
          <div className="tags-test-logs__fade-in">
            <div className="tags-test-logs__slide-in">
              <Table
                aria-label="Retail ID activity details"
                dataSource={retailIdPersisted}
                columns={eventColumns}
                pagination={false}
                size="small"
                rowKey="id"
                showHeader={true}
                bordered={false}
                className="tags-test-logs__minimalistic-table"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const columns = [
    {
      title: <TableColumnTitle text={'Date'} />,
      dataIndex: 'date',
      key: 'date',
    },
  ];

  if (type === 'measurement') {
    columns.push({
      title: <TableColumnTitle text={'Page Views'} />,
      dataIndex: 'pageViews',
      key: 'pageViews',
    });

    columns.push({
      title: <TableColumnTitle text={'Transactions'} />,
      dataIndex: 'transactions',
      key: 'transactions',
    });

    columns.push({
      title: <TableColumnTitle text={'Transaction Total'} />,
      dataIndex: 'total',
      key: 'total',
    });

    columns.push({
      title: <TableColumnTitle text={'Sessions'} />,
      dataIndex: 'sessions',
      key: 'sessions',
    });

    if (hasBasketItems) {
      columns.push({
        title: <TableColumnTitle text={'Basket Items'} />,
        dataIndex: 'basketItems',
        key: 'basketItems',
      });
    }

    if (hasSignups) {
      columns.push({
        title: <TableColumnTitle text={'Sign Ups'} />,
        dataIndex: 'signups',
        key: 'signups',
      });
    }
  } else if (type === 'publisher') {
    columns.push({
      title: <TableColumnTitle text={'Page Views'} />,
      dataIndex: 'pageViews',
      key: 'pageViews',
    });
  } else if (type === 'impression') {
    columns.push({
      title: <TableColumnTitle text={'Impressions'} />,
      dataIndex: 'impressions',
      key: 'impressions',
    });
  }

  return (
    <Card
      title="TEST LOG"
      className="tags-card"
      extra={
        <>
          <Tooltip title="Note: Test Signal logs data from the past 5 days only">
            <Button
              shape={'round'}
              loading={loading}
              type="primary"
              onClick={async () => {
                const validRetailIds = retailIds.filter(retailId => retailId && retailId.trim() !== '');
                const concatenated = validRetailIds.length > 0 ? validRetailIds.join(',') : null;
                message.info(`Testing Signal for: ${concatenated || 'all retail locations'}`);
                await testTag(concatenated);
              }}
            >
              Test Signal
            </Button>
          </Tooltip>
        </>
      }
    >
      <TagMetricCards
        generatedTag={generatedTag}
        type={type}
      />
      <Table
        aria-label="Test signal logs"
        key={basicTagActivityData ? 'with-data' : 'without-data'}
        dataSource={logs?.slice((currentPage - 1) * pageSize, currentPage * pageSize)}
        columns={columns}
        scroll={{ x: "max-content" }}
        size="middle"
        className="tags-test-logs__main-table"
        pagination={false}
        {...(hasRetailIdData() && {
          expandable: {
            expandedRowRender,
            expandedRowKeys,
            onExpand: handleExpand,
            rowExpandable: (record) => {
              return currentPage === 1 && logs.indexOf(record) === 0;
            },
          }
        })}
        rowKey={(record, index) => record.key || index}
      />
      <TagsPagination
        current={currentPage}
        pageSize={pageSize}
        total={logs?.length || 0}
        onChange={setCurrentPage}
        onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
        compact
      />
    </Card>
  );
};

export default TestLogsCard;
